import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbModal, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ParkingLotService } from './parking-lot.service';
import { ParkingLotResponseModel } from './models/parking-lot-response-model';
import { ItemResponseModelV2, ItemResponseModelV2Old, SupplierV2 } from '../items/models/item-response-model-v2';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { ViewSupplierComponent } from '../items/child-components/view-supplier.component';
import { UtilService } from '../common-services/util.service';
import { EnGbTextPipe } from '../utils/en-gb-text.pipe';
import { PersistMessageReferenceService } from '../items/persist-message-reference.service';

@Component({
  selector: 'parking-lot-view',
  standalone: true,
  imports: [CommonModule, NgbNavModule, KENDO_GRID, EnGbTextPipe],
  providers: [ParkingLotService, PersistMessageReferenceService],
  templateUrl: './parking-lot-view.component.html'
})
export class ParkingLotViewComponent implements OnInit {
  @Input() parkingLotModel: ParkingLotResponseModel;
  messagePayload: string = '';
  loading = true;
  error: string = '';
  itemDetail: ItemResponseModelV2Old | null = null;
  itemDetailLoading = true;
  itemDetailError = '';
  originalMessageData: any = null;
  originalMessageLoading = false;
  originalMessageError = '';

  constructor(
    public activeModal: NgbActiveModal,
    private parkingLotService: ParkingLotService,
    private sanitizer: DomSanitizer,
    private modalService: NgbModal,
    public utilService: UtilService,
    private persistMessageReferenceService: PersistMessageReferenceService
  ) {}

  get syntaxHighlightedJson(): SafeHtml {
    let html = this.messagePayload
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    html = html.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
      let cls = 'json-number';
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'json-key';
        } else {
          cls = 'json-string';
        }
      } else if (/true|false/.test(match)) {
        cls = 'json-boolean';
      } else if (/null/.test(match)) {
        cls = 'json-null';
      }
      return '<span class="' + cls + '">' + match + '</span>';
    });

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  public copyToClipboard() {
    navigator.clipboard.writeText(this.messagePayload);
  }

  public viewSupplier(gridRow: SupplierV2) {
    const modalRef = this.modalService.open(ViewSupplierComponent, { size: 'size_60' });
    modalRef.componentInstance.supplierModel = gridRow;
    modalRef.result.then(() => {}, () => {});
  }

  ngOnInit() {
    this.parkingLotService.getParkingLotMessagePayload(this.parkingLotModel.itemId, this.parkingLotModel.replacedByItemId)
      .subscribe({
        next: (result: ParkingLotResponseModel) => {
          try {
            this.messagePayload = JSON.stringify(JSON.parse(result.messagePayload), null, 2);
          } catch {
            this.messagePayload = result.messagePayload; // fallback if not valid JSON
          }
          this.loading = false;
        },
        error: (err) => {
          this.error = 'Failed to load message payload.';
          this.loading = false;
        }
      });

    this.parkingLotService.getParkingLotById(this.parkingLotModel.id)
      .subscribe({
        next: (result: ItemResponseModelV2Old) => {
          this.itemDetail = result;
          this.itemDetailLoading = false;
        },
        error: () => {
          this.itemDetailError = 'Failed to load item details.';
          this.itemDetailLoading = false;
        }
      });

    if (this.parkingLotModel.referenceId) {
      this.originalMessageLoading = true;
      this.persistMessageReferenceService.getPersistMessageContent(this.parkingLotModel.referenceId)
        .subscribe({
          next: (result) => {
            this.originalMessageData = result;
            this.originalMessageLoading = false;
          },
          error: () => {
            this.originalMessageError = 'Failed to load original message.';
            this.originalMessageLoading = false;
          }
        });
    }
  }

  get isOriginalMessageXml(): boolean {
    const raw = typeof this.originalMessageData === 'string' ? this.originalMessageData.trimStart() : '';
    return raw.startsWith('<');
  }

  get formattedOriginalMessage(): string {
    if (this.isOriginalMessageXml) {
      return this.formatXml(typeof this.originalMessageData === 'string' ? this.originalMessageData : String(this.originalMessageData));
    }
    try {
      const parsed = typeof this.originalMessageData === 'string' ? JSON.parse(this.originalMessageData) : this.originalMessageData;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return typeof this.originalMessageData === 'string' ? this.originalMessageData : JSON.stringify(this.originalMessageData, null, 2);
    }
  }

  get syntaxHighlightedOriginalMessage(): SafeHtml {
    if (this.isOriginalMessageXml) {
      return this.highlightXml(this.formattedOriginalMessage);
    }
    const content = this.formattedOriginalMessage;
    let html = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    html = html.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
      let cls = 'json-number';
      if (/^"/.test(match)) {
        cls = /:$/.test(match) ? 'json-key' : 'json-string';
      } else if (/true|false/.test(match)) {
        cls = 'json-boolean';
      } else if (/null/.test(match)) {
        cls = 'json-null';
      }
      return `<span class="${cls}">${match}</span>`;
    });
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private highlightXml(xml: string): SafeHtml {
    let html = xml
      .replace(/&/g, '&amp;')
      .replace(/</g, '\x00LT\x00')
      .replace(/>/g, '\x00GT\x00');

    // Processing instructions / declarations: <?...?>
    html = html.replace(
      /\x00LT\x00(\?)([^\x00]*?)(\?)\x00GT\x00/g,
      (_, open, inner, close) => {
        const piMatch = inner.match(/^([A-Za-z][A-Za-z0-9_:\-.]*)(.*)$/s);
        const piTarget = piMatch ? `<span class="xml-element">${piMatch[1]}</span>` : '';
        const piRest = piMatch ? piMatch[2] : inner;
        const attrHtml = piRest.replace(
          /([A-Za-z][A-Za-z0-9_:\-.]*)=("([^"]*)"|'([^']*)')/g,
          '<span class="xml-pi-attr-name">$1</span>=<span class="xml-attr-value">$2</span>'
        );
        return `<span class="xml-pi-delim">&lt;${open}</span>${piTarget}<span class="xml-decl">${attrHtml}</span><span class="xml-pi-delim">${close}&gt;</span>`;
      }
    );
    // Comments: <!--...-->
    html = html.replace(
      /\x00LT\x00(!--[\s\S]*?--)\x00GT\x00/g,
      '<span class="xml-comment">&lt;$1&gt;</span>'
    );
    // Closing tags: </tagName>
    html = html.replace(
      /\x00LT\x00(\/[A-Za-z][A-Za-z0-9_:\-.]*)\x00GT\x00/g,
      (_, tag) => `<span class="xml-bracket">&lt;</span><span class="xml-element">${tag}</span><span class="xml-bracket">&gt;</span>`
    );
    // Opening / self-closing tags with optional attributes
    html = html.replace(
      /\x00LT\x00([A-Za-z][A-Za-z0-9_:\-.]*)([^\x00]*?)(\/?)\x00GT\x00/g,
      (_, name, attrs, selfClose) => {
        const attrHtml = attrs.replace(
          /([A-Za-z][A-Za-z0-9_:\-.]*)=("([^"]*)"|'([^']*)')/g,
          '<span class="xml-attr-name">$1</span>=<span class="xml-attr-value">$2</span>'
        );
        return `<span class="xml-bracket">&lt;</span><span class="xml-element">${name}</span>${attrHtml}<span class="xml-bracket">${selfClose}&gt;</span>`;
      }
    );
    // Restore any remaining placeholders
    html = html.replace(/\x00LT\x00/g, '&lt;').replace(/\x00GT\x00/g, '&gt;');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  public copyOriginalMessageToClipboard() {
    navigator.clipboard.writeText(this.formattedOriginalMessage);
  }

  private formatXml(xml: string): string {
    let formatted = '';
    let indent = 0;
    const tab = '  ';
    xml = xml.replace(/\r\n|\r/g, '\n').trim();
    const nodes = xml.split(/(<[^>]+>)/);
    for (let node of nodes) {
      node = node.trim();
      if (!node) continue;
      if (/^<\//.test(node)) {
        indent--;
        formatted += tab.repeat(Math.max(indent, 0)) + node + '\n';
      } else if (/^<[^?!][^>]*[^/]>$/.test(node) || /^<[^?!/][^>]*>$/.test(node)) {
        formatted += tab.repeat(indent) + node + '\n';
        indent++;
      } else {
        formatted += tab.repeat(indent) + node + '\n';
      }
    }
    return formatted.trimEnd();
  }
}
